import React, { useState } from "react";
import PersonPicker, { Person } from "../rounds/PersonPicker";
import QuickAddPerson from "../people/QuickAddPerson";
import { HelperMode, HelperModeChips } from "../rounds/helperMode";
import { CLOSE_REASONS } from "../rounds/LoomSheet";
import type { SheetLine } from "../sheet/sheetModel";
import { useKT } from "../i18n/kt";
import type { DailyRow } from "./dailyModel";

/**
 * What a long-press (or a tap on the name) offers for one loom: open its page, change who is on it
 * today, or mark it closed with a reason. A sheet from the bottom on phones.
 */
const RowActions = ({
  row,
  line,
  roster,
  busy,
  onOpen,
  onPeople,
  onCloseLoom,
  onDismiss,
}: {
  row: DailyRow;
  line?: SheetLine;
  roster: Person[];
  busy: boolean;
  onOpen: () => void;
  onPeople: (p: { weaverId: string; helperId: string; helperMode: "ALONE" | "NOT_NEEDED"; people: Person[] }) => void;
  onCloseLoom: (reason: string) => void;
  onDismiss: () => void;
}) => {
  const { t } = useKT();
  const find = (id?: string) => (id ? roster.find((p) => p.id === id) || null : null);
  const asPerson = (p?: { id: string; code: string; name?: string } | null): Person | null => (p ? { id: p.id, code: p.code, name: p.name || "" } : null);
  const [weaver, setWeaver] = useState<Person | null>(find(line?.weaverId) || asPerson(row.weaver));
  const [helper, setHelper] = useState<Person | null>(line?.helperId ? find(line.helperId) || asPerson(row.helper) : null);
  const [mode, setMode] = useState<HelperMode>(line?.helperId ? "WITH_HELPER" : line?.helperMode === "NOT_NEEDED" ? "NOT_NEEDED" : "ALONE");
  const alone = mode !== "WITH_HELPER";
  // Someone not in Workers yet: found by search first, added only from "+ Add".
  const [adding, setAdding] = useState<{ role: "WEAVER" | "HELPER"; name: string } | null>(null);
  const canEditPeople = row.kind === "RUN" && line && line.status !== "SAVED" && line.status !== "SAVING";
  const canClose = row.kind !== "CLOSED" && row.savedEntries === 0;

  return (
    <>
      <div className="kd-backdrop" onClick={busy ? undefined : onDismiss} />
      <section className="kd-actions" role="dialog" aria-modal="true" aria-label={`${t("Loom")} ${row.loomCode}`}>
        <div className="kd-actions__grab" aria-hidden="true" />
        <header className="kd-actions__head">
          <h3>
            {t("Loom")} {row.loomCode}
          </h3>
          {row.sareeNumber ? (
            <span>
              {t("Saree")} {row.sareeNumber}
              {row.designNumber ? ` · ${row.designNumber}` : ""}
            </span>
          ) : null}
        </header>

        <button type="button" className="kd-actions__open" onClick={onOpen}>
          {t("Open loom page")}
          <span aria-hidden="true">›</span>
        </button>

        {canEditPeople ? (
          <div className="kd-actions__group">
            <span className="kd-actions__label">{t("Who is on it today")}</span>
            <PersonPicker
              value={weaver}
              onChange={setWeaver}
              placeholder={t("Weaver: code or name, e.g. K_56")}
              label={t("Weaver")}
              roster={roster}
              onAddNew={(name) => setAdding({ role: "WEAVER", name })}
              addLabel="as a new weaver"
            />
            <HelperModeChips value={mode} onChange={setMode} />
            {!alone ? (
              <PersonPicker
                value={helper}
                onChange={setHelper}
                placeholder={t("Helper: code or name, e.g. J_12")}
                label={t("Helper")}
                roster={roster}
                onAddNew={(name) => setAdding({ role: "HELPER", name })}
                addLabel="as a new helper"
              />
            ) : null}
            <button
              type="button"
              className="kd-btn"
              disabled={!weaver || (!alone && !helper)}
              onClick={() =>
                onPeople({
                  weaverId: weaver?.id || "",
                  helperId: alone ? "" : helper?.id || "",
                  helperMode: mode === "NOT_NEEDED" ? "NOT_NEEDED" : "ALONE",
                  people: [weaver, alone ? null : helper].filter(Boolean) as Person[],
                })
              }
            >
              {t("Use for today")}
            </button>
          </div>
        ) : null}

        {canClose ? (
          <div className="kd-actions__group">
            <span className="kd-actions__label">{t("Loom not working today? Mark it closed")}</span>
            <div className="kd-chips">
              {CLOSE_REASONS.map((r) => (
                <button key={r.key} type="button" className="kr-choice" disabled={busy} onClick={() => onCloseLoom(r.key)}>
                  {t(r.label)}
                </button>
              ))}
            </div>
            <span className="kd-actions__note">{t("A closed loom counts as updated and never goes red.")}</span>
          </div>
        ) : null}

        <button type="button" className="kd-btn kd-btn--line kd-actions__cancel" disabled={busy} onClick={onDismiss}>
          {t("Cancel")}
        </button>
      </section>
      {adding ? (
        <QuickAddPerson
          initialName={adding.name}
          role={adding.role}
          title={adding.role === "WEAVER" ? t("New weaver") : t("New helper")}
          onCancel={() => setAdding(null)}
          onDone={(p) => {
            const person = { id: p.id, code: p.code, name: p.name };
            if (adding.role === "WEAVER") setWeaver(person);
            else setHelper(person);
            setAdding(null);
          }}
        />
      ) : null}
    </>
  );
};

export default RowActions;
